import React from 'react';
import { Card, CardContent } from '../../ui/card';
import { Input } from '../../ui/input';
import { Button } from '../../ui/button';
import {
  Users, BookOpen, Search, UserPlus, Bell, ChevronRight,
  FileText, BarChart3, Wallet, Headphones, Activity, AlertTriangle
} from 'lucide-react';

export const StudentHubContent = ({
  students,
  registrationRequests,
  counselors,
  searchQuery,
  setSearchQuery,
  filteredStudents,
  setActiveSection,
  setSelectedStudent,
  setShowStudentModal
}) => {
  // Hub için özet istatistikler
  const neverLoggedIn = students.filter(s => !s.last_login).length;
  const noAgreement = students.filter(s => !s.agreements_accepted).length;
  const activeToday = students.filter(s => {
    if (!s.last_login) return false;
    const lastLogin = new Date(s.last_login);
    const today = new Date();
    return lastLogin.toDateString() === today.toDateString();
  }).length;

  const hubSections = [
    { id: 'registration-requests', title: 'Kayıt Talepleri', description: 'Bekleyen öğrenci kayıt taleplerini onayla veya reddet', icon: <Bell className="w-6 h-6" />, stats: `${registrationRequests.filter(r => r.status === 'pending').length} Bekleyen`, badge: registrationRequests.filter(r => r.status === 'pending').length > 0 ? registrationRequests.filter(r => r.status === 'pending').length : null, color: 'orange' },
    { id: 'student-list', title: 'Öğrenci Listesi', description: 'Tüm öğrencileri görüntüle, filtrele ve yönet', icon: <Users className="w-6 h-6" />, stats: `${students.length} Kayıtlı`, color: 'blue' },
    { id: 'student-search', title: 'Öğrenci Arama', description: 'İsim, e-posta veya telefon ile öğrenci ara', icon: <Search className="w-6 h-6" />, stats: 'Hızlı Arama', color: 'purple' },
    { id: 'student-register', title: 'Yeni Öğrenci Kayıt', description: 'Tek tek veya Excel ile toplu öğrenci ekle', icon: <UserPlus className="w-6 h-6" />, stats: 'Kayıt Ekle', color: 'green' },
    { id: 'student-management', title: 'Öğrenci Ders Durumu', description: 'Öğrencilerin ders ilerlemeleri ve durumları', icon: <BookOpen className="w-6 h-6" />, stats: 'Ders Takibi', color: 'indigo' },
    { id: 'student-accounting', title: 'Öğrenci Muhasebe', description: 'Ödeme takibi, borç-alacak durumu', icon: <Wallet className="w-6 h-6" />, stats: 'Finans', color: 'emerald' },
    { id: 'student-reports', title: 'Öğrenci Raporları', description: 'İlerleme raporları ve analizler', icon: <BarChart3 className="w-6 h-6" />, stats: 'Raporlar', color: 'cyan' },
    { id: 'counselors', title: 'Eğitim Danışmanları', description: 'Danışman atamaları ve öğrenci dağılımı', icon: <Headphones className="w-6 h-6" />, stats: `${counselors.length} Danışman`, color: 'pink' }
  ];

  const colorClasses = {
    blue: 'bg-blue-100 text-blue-600',
    green: 'bg-green-100 text-green-600',
    purple: 'bg-purple-100 text-purple-600',
    orange: 'bg-orange-100 text-orange-600',
    indigo: 'bg-indigo-100 text-indigo-600',
    emerald: 'bg-emerald-100 text-emerald-600',
    cyan: 'bg-cyan-100 text-cyan-600',
    pink: 'bg-pink-100 text-pink-600',
    amber: 'bg-amber-100 text-amber-600',
    red: 'bg-red-100 text-red-600'
  };

  return (
    <div className="space-y-6">
      {/* Başlık */}
      <div className="flex items-center gap-3 pb-3 border-b border-gray-200">
        <div className="p-2 bg-blue-600 rounded-lg text-white">
          <Users className="w-5 h-5" />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-gray-800">Öğrenci İşlemleri</h2>
          <p className="text-sm text-gray-500">Öğrenci yönetimi ve takibi</p>
        </div>
      </div>

      {/* Özet Kartlar - Sade */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-50 rounded-lg">
              <Users className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-800">{students.length}</p>
              <p className="text-xs text-gray-500">Toplam Öğrenci</p>
            </div>
          </div>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-50 rounded-lg">
              <Activity className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-800">{activeToday}</p>
              <p className="text-xs text-gray-500">Bugün Aktif</p>
            </div>
          </div>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-50 rounded-lg">
              <AlertTriangle className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-800">{neverLoggedIn}</p>
              <p className="text-xs text-gray-500">Hiç Giriş Yapmamış</p>
            </div>
          </div>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-50 rounded-lg">
              <FileText className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-800">{noAgreement}</p>
              <p className="text-xs text-gray-500">Sözleşme Bekleyen</p>
            </div>
          </div>
        </div>
      </div>

      {/* Hızlı Arama */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <div className="flex items-center gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <Input
              placeholder="Öğrenci adı, e-posta veya telefon ile ara..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 h-11"
            />
          </div>
          <Button 
            onClick={() => setActiveSection('student-search')}
            variant="outline"
            className="h-11"
          >
            Gelişmiş Arama
          </Button>
        </div>
        {searchQuery && (
          <div className="mt-4 max-h-60 overflow-y-auto">
            {filteredStudents.slice(0, 5).map(student => (
              <div 
                key={student.id}
                className="flex items-center justify-between p-3 hover:bg-gray-50 rounded-lg cursor-pointer"
                onClick={() => {
                  setSelectedStudent(student);
                  setShowStudentModal(true);
                }}
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                    <span className="text-blue-600 font-semibold">{student.name?.charAt(0)}</span>
                  </div>
                  <div>
                    <p className="font-medium">{student.name}</p>
                    <p className="text-sm text-gray-500">{student.email}</p>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-gray-400" />
              </div>
            ))}
            {filteredStudents.length > 5 && (
              <p className="text-center text-gray-500 text-sm mt-2">
                +{filteredStudents.length - 5} daha fazla sonuç...
              </p>
            )}
          </div>
        )}
      </div>

      {/* Hub Menü Kartları - Renkli İkonlar */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {hubSections.map(section => {
          const iconClass = colorClasses[section.color] || 'bg-blue-100 text-blue-600';
          
          return (
            <Card 
              key={section.id}
              className="border border-gray-200 hover:border-blue-300 hover:shadow-md transition-all cursor-pointer group bg-white"
              onClick={() => setActiveSection(section.id)}
            >
              <CardContent className="p-5">
                <div className="flex items-start gap-4">
                  <div className={`p-3 rounded-lg ${iconClass}`}>
                    {section.icon}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-gray-800 group-hover:text-blue-600 transition-colors">{section.title}</h3>
                      {section.badge && (
                        <span className="px-2 py-0.5 bg-red-500 text-white text-xs rounded-full">{section.badge}</span>
                      )}
                    </div>
                    <p className="text-sm text-gray-500 mt-1 line-clamp-2">{section.description}</p>
                    <span className="inline-block mt-2 text-xs text-gray-400 bg-gray-100 px-2 py-1 rounded">{section.stats}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
};

export default StudentHubContent;
